import React, { useState, useEffect, useRef } from 'react';

const CORES_FAMILIA = {
  social: '#3B82F6',
  dormitorio: '#8B5CF6',
  area_molhada_chuveiro: '#06B6D4',
  area_molhada_seca: '#06B6D4',
  servico: '#F59E0B',
  circulacao: '#6B7280',
  garagem: '#78716C',
  varanda: '#22C55E',
  sacada: '#10B981',
  deposito: '#A3A3A3',
  closet: '#8B5CF6',
};

const CONF_BADGE = {
  alta: { icon: '✓', cor: 'text-green-600' },
  media: { icon: '~', cor: 'text-yellow-600' },
  baixa: { icon: '!', cor: 'text-red-600' },
};

function capitalizar(str) {
  if (!str) return '';
  return str.replace(/_/g, ' ').replace(/\b\w/g, c => c.toUpperCase());
}

function interpolateColor(cor1, cor2, t) {
  const hex = c => parseInt(c, 16);
  const r1 = hex(cor1.slice(1,3)), g1 = hex(cor1.slice(3,5)), b1 = hex(cor1.slice(5,7));
  const r2 = hex(cor2.slice(1,3)), g2 = hex(cor2.slice(3,5)), b2 = hex(cor2.slice(5,7));
  const r = Math.round(r1 + (r2 - r1) * t);
  const g = Math.round(g1 + (g2 - g1) * t);
  const b = Math.round(b1 + (b2 - b1) * t);
  return `rgb(${r},${g},${b})`;
}

export default function PlantaOverlay({ imagemPlanta, comodos, modoVisualizacao = 'familia', onComodoClick, onAcabamentoChange, onEsquadriaChange, onEspecialChange, onAddEsquadria, onRemoveEsquadria, opcoesAcabamento, onBboxUpdate, onComodoFieldChange }) {
  const [hoveredComodo, setHoveredComodo] = useState(null);
  const [mousePos, setMousePos] = useState({ x: 0, y: 0 });
  const [visibleCount, setVisibleCount] = useState(0);
  const [selectedComodo, setSelectedComodo] = useState(null);
  const [editMode, setEditMode] = useState(false);
  const [dragging, setDragging] = useState(null); // { idx, type: 'move'|'nw'|'ne'|'sw'|'se', startMouse, startBbox }
  const frozenViewBox = useRef(null); // eslint-disable-line no-unused-vars
  const [zoom, setZoom] = useState(1);
  const [pan, setPan] = useState({ x: 0, y: 0 });
  const [panning, setPanning] = useState(false);
  const [panStart, setPanStart] = useState({ x: 0, y: 0 });
  const [placingComodo, setPlacingComodo] = useState(null); // índice do cômodo sem bbox sendo posicionado
  const [panMode, setPanMode] = useState(false);
  const [imageAspect, setImageAspect] = useState(null); // eslint-disable-line no-unused-vars
  const [focusMode, setFocusMode] = useState(true); // true = crop inteligente nos cômodos
  const containerRef = useRef(null);

  // Detectar aspect ratio real da imagem
  useEffect(() => {
    if (!imagemPlanta) return;
    const img = new Image();
    img.onload = () => {
      setImageAspect(img.naturalWidth / img.naturalHeight);
    };
    img.src = imagemPlanta;
  }, [imagemPlanta]);

  const SHRINK = 0.12;
  // Clamp bbox para ficar dentro do range 0-1 (evita cômodos extrapolando a planta)
  function clampBbox(bb) {
    let x = Math.max(0, Math.min(1, bb.x));
    let y = Math.max(0, Math.min(1, bb.y));
    let w = Math.min(bb.w, 1 - x);
    let h = Math.min(bb.h, 1 - y);
    if (w < 0.005) w = 0.005;
    if (h < 0.005) h = 0.005;
    return { x, y, w, h };
  }
  const comodosBbox = (comodos || []).map((c, idx) => ({ ...c, _realIdx: idx })).filter(c => c.bbox).map(c => {
    const bb = clampBbox(c.bbox);
    // No modo edição, não aplicar shrink — o que o usuário vê é o que salva
    if (editMode) return { ...c, bbox: { ...bb } };
    const shrinkW = bb.w * SHRINK;
    const shrinkH = bb.h * SHRINK;
    return { ...c, bbox: clampBbox({ x: bb.x + shrinkW / 2, y: bb.y + shrinkH / 2, w: bb.w - shrinkW, h: bb.h - shrinkH }) };
  });

  useEffect(() => {
    setVisibleCount(0);
    const timer = setInterval(() => {
      setVisibleCount(prev => {
        if (prev >= comodosBbox.length) { clearInterval(timer); return prev; }
        return prev + 1;
      });
    }, 150);
    return () => clearInterval(timer);
  }, [comodosBbox.length]);

  if (!imagemPlanta) return null;
  // Se não tem nenhum bbox ainda, mostrar imagem pura + banner pra posicionar
  const temBbox = comodosBbox.length > 0; // eslint-disable-line no-unused-vars

  // Heatmap de custo
  const custosM2 = comodosBbox.filter(c => c.custo_estimado && c.area).map(c => c.custo_estimado / c.area);
  const minCusto = custosM2.length > 0 ? Math.min(...custosM2) : 0;
  const maxCusto = custosM2.length > 0 ? Math.max(...custosM2) : 1;

  function getCorComodo(comodo) {
    if (modoVisualizacao === 'custo' && comodo.custo_estimado && comodo.area) {
      const custoM2 = comodo.custo_estimado / comodo.area;
      const t = (custoM2 - minCusto) / (maxCusto - minCusto || 1);
      return t < 0.5
        ? interpolateColor('#22C55E', '#F59E0B', t * 2)
        : interpolateColor('#F59E0B', '#EF4444', (t - 0.5) * 2);
    }
    return CORES_FAMILIA[comodo.familia] || '#9CA3AF';
  }

  const hoveredData = hoveredComodo !== null ? comodosBbox[hoveredComodo] : null;

  // Familias unicas para legenda
  const familiasUnicas = [...new Set(comodosBbox.map(c => c.familia).filter(Boolean))];

  // Smart crop: calcular envelope dos bboxes com margem
  const CROP_MARGIN = 0.08; // 8% de margem ao redor dos cômodos
  let minX, minY, vbW, vbH;
  if (focusMode && comodosBbox.length > 0) {
    const allX1 = comodosBbox.map(c => c.bbox.x);
    const allY1 = comodosBbox.map(c => c.bbox.y);
    const allX2 = comodosBbox.map(c => c.bbox.x + c.bbox.w);
    const allY2 = comodosBbox.map(c => c.bbox.y + c.bbox.h);
    const envX1 = Math.min(...allX1);
    const envY1 = Math.min(...allY1);
    const envX2 = Math.max(...allX2);
    const envY2 = Math.max(...allY2);
    const envW = envX2 - envX1;
    const envH = envY2 - envY1;
    minX = Math.max(0, envX1 - envW * CROP_MARGIN);
    minY = Math.max(0, envY1 - envH * CROP_MARGIN);
    vbW = Math.min(1 - minX, envW + envW * CROP_MARGIN * 2);
    vbH = Math.min(1 - minY, envH + envH * CROP_MARGIN * 2);
  } else {
    minX = 0;
    minY = 0;
    vbW = 1;
    vbH = 1;
  }

  // --- Funções de drag/resize de bbox ---
  function svgCoordsFromEvent(e, container) {
    const rect = container.getBoundingClientRect();
    const relX = (e.clientX - rect.left) / rect.width;
    const relY = (e.clientY - rect.top) / rect.height;
    // Mapear posição relativa → coordenadas do viewBox atual
    return { x: minX + relX * vbW, y: minY + relY * vbH };
  }

  function handleDragStart(e, idx, type) {
    if (!editMode || !onBboxUpdate) return;
    e.stopPropagation();
    e.preventDefault();
    const container = e.currentTarget.closest('.planta-overlay-container');
    const pos = svgCoordsFromEvent(e, container);
    const bb = comodosBbox[idx].bbox;
    setDragging({ idx, type, startMouse: pos, startBbox: { ...bb }, container });
  }

  function handleDragMove(e) {
    if (!dragging) return;
    const pos = svgCoordsFromEvent(e, dragging.container);
    const dx = pos.x - dragging.startMouse.x;
    const dy = pos.y - dragging.startMouse.y;
    const sb = dragging.startBbox;
    let newBbox;
    if (dragging.type === 'move') {
      newBbox = { x: sb.x + dx, y: sb.y + dy, w: sb.w, h: sb.h };
    } else if (dragging.type === 'se') {
      newBbox = { x: sb.x, y: sb.y, w: Math.max(0.01, sb.w + dx), h: Math.max(0.01, sb.h + dy) };
    } else if (dragging.type === 'sw') {
      newBbox = { x: sb.x + dx, y: sb.y, w: Math.max(0.01, sb.w - dx), h: Math.max(0.01, sb.h + dy) };
    } else if (dragging.type === 'ne') {
      newBbox = { x: sb.x, y: sb.y + dy, w: Math.max(0.01, sb.w + dx), h: Math.max(0.01, sb.h - dy) };
    } else if (dragging.type === 'nw') {
      newBbox = { x: sb.x + dx, y: sb.y + dy, w: Math.max(0.01, sb.w - dx), h: Math.max(0.01, sb.h - dy) };
    }
    if (newBbox) {
      const comodo = comodosBbox[dragging.idx];
      if (comodo._realIdx >= 0) onBboxUpdate(comodo._realIdx, newBbox);
    }
  }

  function handleDragEnd() {
    setDragging(null);
  }

  const handleSize = Math.min(vbW, vbH) * 0.02;

  return (
    <div className="max-w-7xl mx-auto mb-6">
      <div className="flex items-center justify-between mb-2">
        <h3 className="text-lg font-semibold text-[#1E3A5F]">Visualização na Planta</h3>
        <div className="flex items-center gap-2">
          <div className="flex items-center bg-gray-100 rounded-lg">
            <button onClick={() => { setZoom(z => { const nz = Math.max(0.5, z - 0.25); if (nz <= 1) setPan({ x: 0, y: 0 }); return nz; }); }}
              className="px-2 py-1 text-sm font-bold text-gray-600 hover:text-gray-900">−</button>
            <span className="text-xs text-gray-500 min-w-[40px] text-center">{Math.round(zoom * 100)}%</span>
            <button onClick={() => setZoom(z => Math.min(3, z + 0.25))}
              className="px-2 py-1 text-sm font-bold text-gray-600 hover:text-gray-900">+</button>
            {zoom !== 1 && (
              <button onClick={() => { setZoom(1); setPan({ x: 0, y: 0 }); setPanMode(false); }}
                className="px-2 py-1 text-xs text-orange-500 hover:text-orange-700 border-l">Reset</button>
            )}
          </div>
          <button onClick={() => setPanMode(!panMode)}
            className={`px-2 py-1 text-xs rounded-lg font-medium ${panMode ? 'bg-blue-500 text-white' : 'bg-gray-100 text-gray-600 hover:bg-gray-200'}`}
            title="Arrastar para mover a planta">✋ Pan</button>
          {comodosBbox.length > 0 && (
            <button onClick={() => { setFocusMode(!focusMode); setZoom(1); setPan({ x: 0, y: 0 }); }}
              className={`px-2 py-1 text-xs rounded-lg font-medium ${focusMode ? 'bg-orange-500 text-white' : 'bg-gray-100 text-gray-600 hover:bg-gray-200'}`}
              title={focusMode ? 'Mostrar prancha completa' : 'Focar nos cômodos'}>
              {focusMode ? '🔍 Foco' : '🖼️ Completa'}
            </button>
          )}
          {onBboxUpdate && (
            <button onClick={() => setEditMode(!editMode)}
              className={editMode
                ? 'px-3 py-1 text-xs rounded-lg bg-green-500 text-white font-medium'
                : 'px-3 py-1 text-xs rounded-lg bg-[#1E3A5F] text-white hover:bg-[#2d4f7a]'}>
              {editMode ? '✓ Salvar posições' : '✏️ Ajustar boxes'}
            </button>
          )}
        </div>
      </div>
      {editMode && <p className="text-xs text-orange-600 mb-2">Arraste para mover. Arraste os cantos para redimensionar.</p>}
      {(() => {
        const semBbox = (comodos || []).filter(c => !c.bbox).map((c, i) => ({ ...c, _origIdx: (comodos || []).indexOf(c) }));
        if (semBbox.length === 0) return null;
        return (
          <div className="mb-2 p-2 bg-yellow-50 border border-yellow-200 rounded-lg">
            <p className="text-xs font-semibold text-yellow-800 mb-1">Cômodos sem posição na planta:</p>
            <div className="flex flex-wrap gap-1">
              {semBbox.map((c, i) => (
                <button key={i}
                  onClick={() => { setPlacingComodo(c._origIdx); setEditMode(true); }}
                  className={`text-xs px-2 py-1 rounded-lg ${placingComodo === c._origIdx ? 'bg-orange-500 text-white' : 'bg-yellow-100 text-yellow-800 hover:bg-yellow-200'}`}>
                  {c.nome} ({c.area}m²)
                </button>
              ))}
            </div>
            {placingComodo !== null && <p className="text-xs text-orange-600 mt-1 font-medium">Clique na planta para posicionar "{(comodos || [])[placingComodo]?.nome}"</p>}
          </div>
        );
      })()}
      <div
        ref={containerRef}
        className="relative rounded-lg overflow-hidden planta-overlay-container"
        style={{ cursor: placingComodo !== null ? 'crosshair' : (panning ? 'grabbing' : (panMode ? 'grab' : 'default')) }}
        onMouseDown={e => {
          // Criar bbox: quando está posicionando um cômodo
          if (placingComodo !== null && e.button === 0 && !e.target.closest('.painel-inline')) {
            e.preventDefault();
            const innerDiv = e.currentTarget.querySelector('.planta-inner');
            if (innerDiv && onBboxUpdate) {
              const rect = innerDiv.getBoundingClientRect();
              const relX = (e.clientX - rect.left) / rect.width;
              const relY = (e.clientY - rect.top) / rect.height;
              // Criar bbox centralizado no clique, tamanho padrão baseado na área
              const defaultSize = 0.08;
              const newBbox = {
                x: minX + relX * vbW - defaultSize / 2,
                y: minY + relY * vbH - defaultSize / 2,
                w: defaultSize,
                h: defaultSize,
              };
              onBboxUpdate(placingComodo, newBbox);
              setPlacingComodo(null);
            }
            return;
          }
          // Pan: quando panMode está ativo, e clicou no fundo (não no painel)
          if (panMode && !dragging && e.button === 0 && !e.target.closest('.painel-inline')) {
            e.preventDefault();
            setPanning(true);
            setPanStart({ x: e.clientX - pan.x, y: e.clientY - pan.y });
          }
        }}
        onMouseMove={e => {
          const rect = e.currentTarget.getBoundingClientRect();
          setMousePos({ x: e.clientX - rect.left, y: e.clientY - rect.top });
          if (dragging) handleDragMove(e);
          if (panning) {
            setPan({ x: e.clientX - panStart.x, y: e.clientY - panStart.y });
          }
        }}
        onMouseUp={() => { handleDragEnd(); setPanning(false); }}
        onMouseLeave={() => { handleDragEnd(); setPanning(false); }}
      >
      <div
        className="relative rounded-lg planta-inner"
        style={{
          transform: `scale(${zoom}) translate(${pan.x / zoom}px, ${pan.y / zoom}px)`,
          transformOrigin: 'center center',
        }}
      >
        <svg
          style={{
            width: '100%',
            display: 'block',
            borderRadius: '0.5rem',
            // Aspect ratio: usa o da imagem real se disponível, senão 4:3
            aspectRatio: imageAspect ? `${vbW / vbH * imageAspect}` : `${vbW / vbH * 1.33}`,
          }}
          viewBox={`${minX} ${minY} ${vbW} ${vbH}`}
        >
          {/* Imagem da planta como fundo do SVG — preserveAspectRatio="none" para preencher 0-1 */}
          <image href={imagemPlanta} x="0" y="0" width="1" height="1" preserveAspectRatio="none" />
          {comodosBbox.map((c, i) => {
            const bb = c.bbox;
            const cor = getCorComodo(c);
            const isProjecao = c.tipo_escada === 'projecao';
            const isBaixa = c.confianca === 'baixa';
            const isHovered = hoveredComodo === i;
            const isVisible = i < visibleCount;

            // Smart fontSize
            let fontSize = Math.min(bb.w, bb.h) * 0.14;
            if (fontSize > 0.035) fontSize = 0.035;
            if (fontSize < 0.008) fontSize = 0.008;
            const nome = c.nome || '';
            if (nome.length > 10) fontSize *= 0.75;
            if (nome.length > 15) fontSize *= 0.8;
            const label = (bb.w * bb.h < 0.005 && nome.includes(' ')) ? nome.split(' ')[0] : nome;
            const showLabel = !isProjecao && fontSize >= 0.006;

            const baseOpacity = isProjecao ? 0.08 : isHovered ? 0.40 : 0.25;

            return (
              <g
                key={i}
                className="cursor-pointer"
                style={{ opacity: isVisible ? 1 : 0, transition: 'opacity 0.4s ease-out' }}
                onMouseEnter={() => setHoveredComodo(i)}
                onMouseLeave={() => setHoveredComodo(null)}
                onClick={() => {
                  setSelectedComodo(selectedComodo === i ? null : i);
                  if (onComodoClick) onComodoClick(c, i);
                }}
              >
                <rect
                  x={bb.x}
                  y={bb.y}
                  width={bb.w}
                  height={bb.h}
                  fill={cor}
                  fillOpacity={baseOpacity}
                  stroke={cor}
                  strokeWidth={0.003}
                  strokeDasharray={isBaixa ? '0.01 0.005' : 'none'}
                />
                {showLabel && (
                  <text
                    x={bb.x + bb.w / 2}
                    y={bb.y + bb.h / 2}
                    textAnchor="middle"
                    dominantBaseline="central"
                    fill="#1E3A5F"
                    stroke="white"
                    strokeWidth={fontSize * 0.25}
                    paintOrder="stroke"
                    fontSize={fontSize}
                    fontWeight="700"
                  >
                    {label}
                  </text>
                )}
                {/* Drag body + resize handles no modo edição */}
                {editMode && selectedComodo === i && (
                  <>
                    {/* Drag body (rect invisível por cima) */}
                    <rect x={bb.x} y={bb.y} width={bb.w} height={bb.h}
                      fill="transparent" stroke="#F97316" strokeWidth={0.004} strokeDasharray="0.008 0.004"
                      style={{ cursor: 'move' }}
                      onMouseDown={e => handleDragStart(e, i, 'move')} />
                    {/* Handles nos cantos */}
                    {[
                      { cx: bb.x, cy: bb.y, type: 'nw', cursor: 'nw-resize' },
                      { cx: bb.x + bb.w, cy: bb.y, type: 'ne', cursor: 'ne-resize' },
                      { cx: bb.x, cy: bb.y + bb.h, type: 'sw', cursor: 'sw-resize' },
                      { cx: bb.x + bb.w, cy: bb.y + bb.h, type: 'se', cursor: 'se-resize' },
                    ].map(h => (
                      <circle key={h.type} cx={h.cx} cy={h.cy} r={handleSize}
                        fill="#F97316" stroke="white" strokeWidth={handleSize * 0.3}
                        style={{ cursor: h.cursor }}
                        onMouseDown={e => handleDragStart(e, i, h.type)} />
                    ))}
                  </>
                )}
              </g>
            );
          })}
        </svg>

        {/* Tooltip */}
        {hoveredData && (
          <div
            className="absolute bg-white rounded-lg shadow-lg p-3 z-50 pointer-events-none max-w-xs text-xs"
            style={{ left: mousePos.x + 12, top: mousePos.y - 10 }}
          >
            <p className="font-semibold text-sm text-foreground mb-1">{hoveredData.nome}</p>
            <p className="text-muted-foreground">Área: {hoveredData.area} m²</p>
            <p className="text-muted-foreground">Família: {capitalizar(hoveredData.familia)}</p>
            {hoveredData.confianca && (() => {
              const b = CONF_BADGE[hoveredData.confianca];
              return b ? <p className={b.cor}>Confiança: {b.icon} {capitalizar(hoveredData.confianca)}</p> : null;
            })()}
            {hoveredData.acabamento_padrao && (
              <div className="mt-1 pt-1 border-t border-gray-100">
                <p className="text-muted-foreground">Piso: {hoveredData.acabamento_padrao.piso}</p>
                <p className="text-muted-foreground">Parede: {hoveredData.acabamento_padrao.revParede}</p>
                <p className="text-muted-foreground">Forro: {hoveredData.acabamento_padrao.forro}</p>
              </div>
            )}
          </div>
        )}

        {/* Painel de edição inline — esconder no modo edição de boxes */}
        {selectedComodo !== null && !editMode && comodosBbox[selectedComodo] && (() => {
          const sc = comodosBbox[selectedComodo];
          const acab = sc.acabamento || {};
          const esqs = sc.esquadrias || [];
          const esp = sc.especiais || {};
          const scIdx = sc._realIdx;
          const bb = sc.bbox;
          const panelLeftPct = (bb.x + bb.w - minX) / vbW * 100;
          const panelTopPct = (bb.y + bb.h / 2 - minY) / vbH * 100;
          const precisaAltura = ['Cerâmica', 'Porcelanato', 'Pedra natural'].includes(acab.tipoRevParede);
          // Posicionar: horizontal — direita do bbox ou esquerda se não cabe
          // Vertical — centralizar no bbox, mas se tá na metade de baixo, alinhar pelo bottom
          const goLeft = panelLeftPct > 60;
          const goUp = panelTopPct > 50;
          const panelStyle = {
            left: goLeft ? `${(bb.x - minX) / vbW * 100 - 1}%` : `${panelLeftPct + 1}%`,
            transform: goLeft ? 'translateX(-100%)' : 'none',
          };
          if (goUp) {
            panelStyle.bottom = `${100 - panelTopPct - 20}%`;
          } else {
            panelStyle.top = `${Math.max(0, panelTopPct - 10)}%`;
          }
          const sel = 'w-full text-xs border rounded-lg px-2 py-1.5 bg-gray-50';
          return (
            <div className="absolute bg-white rounded-xl shadow-2xl border border-gray-200 p-4 z-[60] w-72 max-h-[500px] overflow-y-auto painel-inline" style={panelStyle}>
              <div className="flex justify-between items-center mb-3">
                <div className="flex-1">
                  <input type="text" value={sc.nome || ''}
                    onChange={e => onComodoFieldChange && onComodoFieldChange(scIdx, 'nome', e.target.value)}
                    className="font-bold text-sm text-[#1E3A5F] bg-transparent border-b border-transparent hover:border-gray-300 focus:border-orange-400 focus:outline-none w-full"
                    title="Nome do cômodo"
                  />
                  <div className="flex items-center gap-1 text-xs text-gray-500">
                    <input type="number" step="0.01" min="0"
                      value={sc.area || ''}
                      onChange={e => onComodoFieldChange && onComodoFieldChange(scIdx, 'area', parseFloat(e.target.value) || 0)}
                      className="w-14 px-1 py-0.5 border rounded bg-gray-50 text-xs text-gray-700 font-medium"
                      title="Área (m²)"
                    />
                    <span>m² • {sc.familia ? sc.familia.replace(/_/g, ' ') : ''}</span>
                  </div>
                </div>
                <div className="flex items-center gap-1">
                  {onBboxUpdate && (
                    <button onClick={(e) => { e.stopPropagation(); onBboxUpdate(scIdx, null); setSelectedComodo(null); }}
                      className="text-red-400 hover:text-red-600 text-xs px-1.5 py-0.5 rounded border border-red-200 hover:border-red-400" title="Remover posição da planta">🗑</button>
                  )}
                  <button onClick={(e) => { e.stopPropagation(); setSelectedComodo(null); }}
                    className="text-gray-400 hover:text-gray-600 text-lg leading-none">✕</button>
                </div>
              </div>
              {onAcabamentoChange && opcoesAcabamento && scIdx >= 0 ? (
                <div className="space-y-3">
                  <p className="text-xs font-bold text-gray-400 uppercase tracking-wide">Acabamentos</p>
                  <div className="space-y-2">
                    <div>
                      <label className="text-xs text-gray-500 block mb-0.5">Piso</label>
                      <select value={acab.tipoPiso || ''} onChange={e => onAcabamentoChange(scIdx, 'tipoPiso', e.target.value)} className={sel}>
                        <option value="">Selecionar...</option>
                        {(opcoesAcabamento.piso || []).map(o => <option key={o} value={o}>{o}</option>)}
                      </select>
                    </div>
                    <div>
                      <label className="text-xs text-gray-500 block mb-0.5">Rodapé</label>
                      <select value={acab.tipoRodape || 'Sem rodapé'} onChange={e => onAcabamentoChange(scIdx, 'tipoRodape', e.target.value)} className={sel}>
                        {(opcoesAcabamento.rodape || []).map(o => <option key={o} value={o}>{o}</option>)}
                      </select>
                    </div>
                    <div>
                      <label className="text-xs text-gray-500 block mb-0.5">Forro</label>
                      <select value={acab.forro || ''} onChange={e => onAcabamentoChange(scIdx, 'forro', e.target.value)} className={sel}>
                        <option value="">Selecionar...</option>
                        {(opcoesAcabamento.forro || []).map(o => <option key={o} value={o}>{o}</option>)}
                      </select>
                    </div>
                    <div>
                      <label className="text-xs text-gray-500 block mb-0.5">Rev. Parede</label>
                      <select value={acab.tipoRevParede || ''} onChange={e => onAcabamentoChange(scIdx, 'tipoRevParede', e.target.value)} className={sel}>
                        <option value="">Selecionar...</option>
                        {(opcoesAcabamento.revParede || []).map(o => <option key={o} value={o}>{o}</option>)}
                      </select>
                    </div>
                    {precisaAltura && (
                      <div>
                        <label className="text-xs text-gray-500 block mb-0.5">Altura Rev.</label>
                        <select value={acab.alturaRevestimento || ''} onChange={e => onAcabamentoChange(scIdx, 'alturaRevestimento', e.target.value)} className={sel}>
                          <option value="">Selecionar...</option>
                          {(opcoesAcabamento.alturaRev || []).map(o => <option key={o} value={o}>{o}</option>)}
                        </select>
                      </div>
                    )}
                    <div>
                      <label className="text-xs text-gray-500 block mb-0.5">Reboco</label>
                      <select value={acab.tipoReboco || 'Massa corrida PVA'} onChange={e => onAcabamentoChange(scIdx, 'tipoReboco', e.target.value)} className={sel}>
                        {(opcoesAcabamento.reboco || []).map(o => <option key={o} value={o}>{o}</option>)}
                      </select>
                    </div>
                  </div>

                  <p className="text-xs font-bold text-gray-400 uppercase tracking-wide pt-2 border-t">Esquadrias</p>
                  <div className="space-y-2">
                    {esqs.length === 0 && <p className="text-xs text-gray-400 italic">Nenhuma esquadria</p>}
                    {esqs.map((esq, j) => (
                      <div key={j} className="space-y-1 pb-2 border-b border-gray-100 last:border-0">
                        <div className="flex items-center gap-1">
                          <select value={esq.tipo || 'Porta'} onChange={e => onEsquadriaChange && onEsquadriaChange(scIdx, j, 'tipo', e.target.value)}
                            className="flex-1 text-xs border rounded-lg px-1.5 py-1 bg-gray-50">
                            <option>Porta</option><option>Porta de correr</option><option>Porta de enrolar</option>
                            <option>Janela</option><option>Janela de correr</option><option>Basculante</option><option>Vidro fixo</option>
                          </select>
                          <input type="number" min="1" value={esq.quantidade || 1}
                            onChange={e => onEsquadriaChange && onEsquadriaChange(scIdx, j, 'quantidade', parseInt(e.target.value) || 1)}
                            className="w-8 text-center text-xs border rounded-lg py-1 bg-gray-50" title="Qtd" />
                          <button onClick={(e) => { e.stopPropagation(); onRemoveEsquadria && onRemoveEsquadria(scIdx, j); }}
                            className="text-red-400 hover:text-red-600 text-xs leading-none">✕</button>
                        </div>
                        <div className="flex items-center gap-1">
                          <select value={esq.material || ''} onChange={e => onEsquadriaChange && onEsquadriaChange(scIdx, j, 'material', e.target.value)}
                            className="flex-1 text-xs border rounded-lg px-1.5 py-1 bg-gray-50">
                            <option value="">Material...</option>
                            {(opcoesAcabamento.matEsquadria || []).map(o => <option key={o} value={o}>{o}</option>)}
                          </select>
                          <input value={esq.dimensoes || ''} onChange={e => onEsquadriaChange && onEsquadriaChange(scIdx, j, 'dimensoes', e.target.value)}
                            placeholder="0.90x2.10" className="w-20 text-xs border rounded-lg px-1.5 py-1 bg-gray-50 font-mono" title="LxA (m)" />
                        </div>
                      </div>
                    ))}
                    <div className="flex gap-2 pt-1">
                      <button onClick={(e) => { e.stopPropagation(); onAddEsquadria && onAddEsquadria(scIdx, 'Porta'); }}
                        className="text-xs text-orange-500 hover:text-orange-700 font-medium">+ Porta</button>
                      <button onClick={(e) => { e.stopPropagation(); onAddEsquadria && onAddEsquadria(scIdx, 'Janela'); }}
                        className="text-xs text-orange-500 hover:text-orange-700 font-medium">+ Janela</button>
                    </div>
                  </div>

                  <p className="text-xs font-bold text-gray-400 uppercase tracking-wide pt-2 border-t">Bancadas</p>
                  <div className="space-y-2">
                    <div>
                      <label className="text-xs text-gray-500 block mb-0.5">Bancada</label>
                      <select value={esp.bancada || ''} onChange={e => onEspecialChange && onEspecialChange(scIdx, 'bancada', e.target.value)} className={sel}>
                        <option value="">Sem bancada</option>
                        {(opcoesAcabamento.bancada || []).map(o => <option key={o} value={o}>{o}</option>)}
                      </select>
                    </div>
                    <div>
                      <label className="text-xs text-gray-500 block mb-0.5">Pia / Cuba</label>
                      <select value={esp.pia || ''} onChange={e => onEspecialChange && onEspecialChange(scIdx, 'pia', e.target.value)} className={sel}>
                        <option value="">Sem pia</option>
                        {(opcoesAcabamento.pia || []).map(o => <option key={o} value={o}>{o}</option>)}
                      </select>
                    </div>
                    <div>
                      <label className="text-xs text-gray-500 block mb-0.5">Soleira</label>
                      <select value={esp.soleira || ''} onChange={e => onEspecialChange && onEspecialChange(scIdx, 'soleira', e.target.value)} className={sel}>
                        <option value="">Sem soleira</option>
                        {(opcoesAcabamento.soleira || []).map(o => <option key={o} value={o}>{o}</option>)}
                      </select>
                    </div>
                  </div>
                </div>
              ) : (
                <div className="text-xs text-gray-500 space-y-1">
                  <p>Piso: {acab.tipoPiso || '—'}</p>
                  <p>Forro: {acab.forro || '—'}</p>
                  <p>Parede: {acab.tipoRevParede || '—'}</p>
                </div>
              )}
            </div>
          );
        })()}
      </div>
      </div>

      {/* Legenda */}
      <div className="mt-3">
        {modoVisualizacao === 'familia' ? (
          <div className="flex flex-wrap gap-3 items-center">
            {familiasUnicas.map(f => (
              <div key={f} className="flex items-center gap-1.5">
                <div className="w-3 h-3 rounded-sm" style={{ backgroundColor: CORES_FAMILIA[f] || '#9CA3AF' }} />
                <span className="text-xs text-muted-foreground">{capitalizar(f)}</span>
              </div>
            ))}
          </div>
        ) : (
          custosM2.length > 0 ? (
            <div className="max-w-md mx-auto">
              <div className="h-2 rounded-full w-full" style={{ background: 'linear-gradient(to right, #22C55E, #F59E0B, #EF4444)' }} />
              <div className="flex justify-between mt-1">
                <span className="text-xs text-muted-foreground">R$ {minCusto.toFixed(0)}/m²</span>
                <span className="text-xs text-muted-foreground">R$ {maxCusto.toFixed(0)}/m²</span>
              </div>
            </div>
          ) : (
            <p className="text-xs text-muted-foreground text-center">Custos não disponíveis neste modo</p>
          )
        )}
      </div>
    </div>
  );
}